import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Typography,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';

interface AgreementCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const AgreementCheckbox: React.FC<AgreementCheckboxProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePreview = async (filename: string) => {
    setLoading(true);
    setPreviewOpen(true);
    
    // 获取文件URL用于预览（使用HTML预览接口）
    let previewEndpoint = '';
    if (filename === 'user-agreement') {
      previewEndpoint = 'user-agreement-preview';
    } else if (filename === 'privacy-policy') {
      previewEndpoint = 'privacy-policy-preview';
    } else if (filename === 'confidentiality-agreement') {
      previewEndpoint = 'confidentiality-agreement-preview';
    }
    
    const fileUrl = `${window.location.origin}/api/download/${previewEndpoint}`;
    setPreviewUrl(fileUrl);
    
    // 延迟取消loading，给iframe加载时间
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          sx={{
            '&.Mui-checked': {
              color: 'primary.main',
            },
          }}
        />
        <Typography variant="body2" sx={{ fontSize: '14px', lineHeight: 1.5, ml: 1 }}>
          我已阅读并同意
          <MuiLink
            onClick={() => handlePreview('user-agreement')}
            sx={{
              color: '#1976d2 !important',
              textDecoration: 'none',
              cursor: 'pointer',
              mx: 0.5,
              fontWeight: 'bold',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1565c0 !important',
              },
            }}
          >
            《用户协议》
          </MuiLink>
          、《
          <MuiLink
            onClick={() => handlePreview('privacy-policy')}
            sx={{
              color: '#1976d2 !important',
              textDecoration: 'none',
              cursor: 'pointer',
              mx: 0.5,
              fontWeight: 'bold',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1565c0 !important',
              },
            }}
          >
            隐私条款
          </MuiLink>
          》与
          <MuiLink
            onClick={() => handlePreview('confidentiality-agreement')}
            sx={{
              color: '#1976d2 !important',
              textDecoration: 'none',
              cursor: 'pointer',
              mx: 0.5,
              fontWeight: 'bold',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1565c0 !important',
              },
            }}
          >
            《保密与数据安全协议》
          </MuiLink>
        </Typography>
      </Box>

      {/* 预览对话框 */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden', height: 'calc(80vh - 80px)' }}>
          {loading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255,255,255,0.9)',
              zIndex: 1000
            }}>
              <CircularProgress />
            </Box>
          )}
          <iframe
            src={previewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block'
            }}
            title="协议预览"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>关闭</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgreementCheckbox;
